import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import ParcelInfo from 'components/generics/Map/ParcelInfo';
import { useContext } from 'react';
import { ExploitationContext } from 'utils/context';
import { emptyCulture } from 'utils/culturesColors';
import { getCultureCampaignKeyFromYear } from 'utils/Datagrid/CustomParcelstDataGridUtils';
import { filterParcelFromActivityDate } from 'utils/Datagrid/CustomParcelstDataGridUtils';
import { ORGANIC_STATES } from 'utils/GlobalVariables';
import './Map.style.scss';
import MapTab from './MapTab';
import MapInteractionHook from './MapInteraction.hook';

function MapParcels({
  updateParcel,
  forecastCampaign,
  name = 'map',
  fromProcedure = false,
  parcelListData,
}) {
  const { exploitationContext } = useContext(ExploitationContext);
  const { t } = useTranslation();
  const parcelsData = useMemo(() => {
    if (fromProcedure) {
      return parcelListData || [];
    }
    return exploitationContext?.parcels
      ? filterParcelFromActivityDate(exploitationContext?.parcels, forecastCampaign)
      : [];
  }, [fromProcedure, parcelListData, exploitationContext?.parcels]);

  const getCultureForParcel = (parcel, year) => {
    const culture = parcel?.parcelCultures[getCultureCampaignKeyFromYear(year)];
    if (!culture?.name) {
      return { ...emptyCulture, year };
    }
    return culture;
  };

  const getPolygonStyleParams = (parcel, culture, opacity) => {
    return {
      color: Array.isArray(culture) ? culture : (culture?.name ?? culture),
      opacity,
      organicState: parcel?.organicState,
    };
  };

  const onParcelClick = (parcel) => {
    if (parcel) {
      updateParcel(parcel.id);
    }
  };

  const mapState = MapInteractionHook({
    mapId: name,
    parcelsData,
    initialYear: forecastCampaign.startYear,
    minYear: forecastCampaign.startYear - 1,
    maxYear: forecastCampaign.startYear,
    getCultureForParcel,
    getPolygonStyleParams,
    onParcelClick,
  });

  const hasParcelOrganic = parcelsData.some(
    (parcel) =>
      parcel?.organicState === ORGANIC_STATES.organic ||
      parcel?.organicState === ORGANIC_STATES.conversion,
  );

  return (
    <div>
      {hasParcelOrganic && (
        <div className="legend_map_container" style={{ margin: '0 80px' }}>
          <div className="legend_title">{t('label.mapParcelLegend')}</div>
          <div className="legend_map_row">
            <div className="outline_legend">
              <div className="stroke conversion"></div>
              <p>{t('simulation.result.common.bioConversion')}</p>
            </div>
            <div className="outline_legend">
              <div className="stroke organic"></div>
              <p>{t('simulation.common.bioCulture')}</p>
            </div>
            <div className="outline_legend">
              <div className="stroke conventional"></div>
              <p>{t('label.conventionalCulture')}</p>
            </div>
          </div>
        </div>
      )}
      <div className="tab_container">
        {!fromProcedure && (
          <div className="details_container">
            <MapTab
              data={parcelsData}
              header={['', t('common.culture'), t('common.surface')]}
              isDynamic={true}
              year={mapState.year}
              minYear={mapState.minYear}
              maxYear={mapState.maxYear}
              setYear={mapState.setYear}
              parcelHoverCultureName={mapState.parcelHoverCultureName}
              parentCallback={mapState.handleCultureHover}
              fromParcels
            />
          </div>
        )}
        <div ref={mapState.mapElement} className="map_container">
          <div id={name} className="map_element" onMouseOut={mapState.clearPopup}></div>

          {mapState.parcelInfo && mapState.modalCoords && (
            <div ref={mapState.popup} style={mapState.getPopupStyle()}>
              <div
                ref={mapState.popupContentRef}
                className="ol-popup"
                style={mapState.getPopupInnerStyle()}
              >
                <ParcelInfo
                  parcelInfo={mapState.parcelInfo}
                  from="mapParcels"
                  forecastCampaign={forecastCampaign}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MapParcels;
